import React, { useState } from "react";
import { MdRocketLaunch } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { FREE_TRIAL_FEATURES, TRIAL_VIDEO_URL } from "../../constants";
import { getTrialCopy, getTrialCopyFromLaunchFlag } from "../../utils/trialCopy";
import type { PricingCatalog } from "../../utils/pricingCatalog";

interface FreeTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActivateTrial?: (params: { interval: "monthly" | "quarterly" | "yearly" }) => void;
  onScheduleCall?: () => void;
  prefillEmail?: string;
  /** When true, show early-joiner 1.5-month copy; otherwise 7-day paid trial */
  isLaunchFreeBilling?: boolean;
  /** Full catalog — preferred for Safepay vs Stripe trial copy */
  pricingCatalog?: PricingCatalog | null;
}

const FreeTrialModal: React.FC<FreeTrialModalProps> = ({
  isOpen,
  onClose,
  onActivateTrial,
  onScheduleCall,
  prefillEmail,
  isLaunchFreeBilling = false,
  pricingCatalog = null,
}) => {
  const navigate = useNavigate();
  const [interval, setInterval] = useState<"monthly" | "quarterly" | "yearly">("monthly");
  const homepageCampaignPreviewUrl = "/videos/updated_gif.gif";
  const trialCopy = pricingCatalog
    ? getTrialCopy(pricingCatalog)
    : getTrialCopyFromLaunchFlag(isLaunchFreeBilling);

  const handleActivateTrialClick = () => {
    if (onActivateTrial) {
      onActivateTrial({ interval });
      return;
    }
    onClose();
    navigate("/settings/billing");
  };

  if (!isOpen) return null;

  const videoBgGradient = "linear-gradient(180deg, rgba(167, 248, 44, 0.87) -18.53%, rgba(255, 255, 255, 0) 113.79%)";

  return (
    <>
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-3 overflow-x-hidden">
      <div
        className="relative bg-white shadow-xl overflow-hidden flex flex-col"
        style={{
          width: 766,
          height: "auto",
          maxWidth: "100%",
          maxHeight: "calc(100vh - 24px)",
          borderRadius: "8px",
          opacity: 1,
          padding: "17px 16px",
          boxSizing: "border-box",
        }}
      >
        {/* Inner bordered content box: 724×488 */}
        <div
          className="w-full flex flex-col overflow-hidden min-w-0"
          style={{
            width: 724,
            maxWidth: "100%",
            height: "auto",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            opacity: 1,
            flexShrink: 1,
          }}
        >
          {/* 1. Close button - first component, near top */}
          <div className="flex justify-end w-full pr-3 pt-3">
            <button
              onClick={onClose}
              className="flex items-center justify-center flex-shrink-0 hover:opacity-80 transition-opacity"
              style={{
                width: 24,
                height: 24,
                borderRadius: 32,
                border: "1px solid #D9D9D9",
                opacity: 1,
              }}
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#565758]"
                style={{ width: 12.73, height: 12.73 }}
              >
                <path d="M4 4l12 12M16 4L4 16" />
              </svg>
            </button>
          </div>

          {/* 2. Content below - scrolls inside if needed */}
          <div className="flex flex-col sm:flex-row overflow-x-hidden">
            {/* Left: Free trial info - 40% */}
            <div className="flex-1 flex flex-col min-w-0 px-5 sm:px-6 py-6 sm:py-8 w-full sm:w-[40%] sm:flex-shrink-0">
              <div
                className="flex items-center justify-center mb-6 self-center"
                style={{
                  width: 53,
                  height: 53,
                  borderRadius: 18.27,
                  gap: 11.42,
                  opacity: 1,
                  padding: 17.12,
                  background: "linear-gradient(227.02deg, #0F1011 1.65%, #697077 98.35%)",
                }}
              >
                <MdRocketLaunch className="text-white text-2xl flex-shrink-0" />
              </div>
              <h2
                className="mb-1 text-center capitalize"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "24px",
                  lineHeight: "24px",
                  letterSpacing: "0%",
                  color: "#111",
                }}
              >
                $0 For{" "}
                <span style={{ color: "#4285F4" }}>{trialCopy.headlineAccent}</span>
              </h2>
              <p
                className="mb-6 lowercase text-center"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#4b5563",
                }}
              >
                {trialCopy.subtitle}
              </p>
              <ul className="space-y-3 mb-6">
                {FREE_TRIAL_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-gray-700" style={{ fontSize: "12px" }}>
                    <span className="w-5 h-5 rounded-full bg-[#E0E7FF] flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-3 h-3"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="#4285F4"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 6l3 3 5-6" />
                      </svg>
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to="/settings/billing"
                onClick={onClose}
                className="mb-6 text-center hover:underline"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#5C7AF7",
                }}
              >
                See pricing details
              </Link>

              {/* Monthly / Quarterly / Yearly toggle (temporarily hidden) */}
              {/* <div
                className="mb-4 self-center px-1"
                style={{
                  width: 272,
                  height: 36,
                  borderRadius: 9999,
                  background: "#f1f5f9",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  padding: 2,
                  boxSizing: "border-box",
                  userSelect: "none",
                }}
                role="tablist"
                aria-label="Billing interval"
              >
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 2,
                    left: interval === "monthly" ? 2 : interval === "quarterly" ? 91 : 180,
                    width: 89,
                    height: 32,
                    borderRadius: 9999,
                    background: "#74a56a",
                    transition: "left 160ms ease",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setInterval("monthly")}
                  style={{
                    width: 89,
                    height: 32,
                    borderRadius: 9999,
                    zIndex: 1,
                    color: interval === "monthly" ? "#ffffff" : "#334155",
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                    lineHeight: "100%",
                    background: "transparent",
                  }}
                  role="tab"
                  aria-selected={interval === "monthly"}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setInterval("quarterly")}
                  style={{
                    width: 89,
                    height: 32,
                    borderRadius: 9999,
                    zIndex: 1,
                    color: interval === "quarterly" ? "#ffffff" : "#334155",
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                    lineHeight: "100%",
                    background: "transparent",
                  }}
                  role="tab"
                  aria-selected={interval === "quarterly"}
                >
                  Quarterly -15%
                </button>
                <button
                  type="button"
                  onClick={() => setInterval("yearly")}
                  style={{
                    width: 89,
                    height: 32,
                    borderRadius: 9999,
                    zIndex: 1,
                    color: interval === "yearly" ? "#ffffff" : "#334155",
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                    lineHeight: "100%",
                    background: "transparent",
                  }}
                  role="tab"
                  aria-selected={interval === "yearly"}
                >
                  Yearly -25%
                </button>
              </div> */}

              <button
                onClick={handleActivateTrialClick}
                className="self-center capitalize transition-colors shadow-sm bg-[#A3FF6E] hover:bg-[#8fef5a]"
                style={{
                  width: 257,
                  height: 42,
                  borderRadius: 8,
                  gap: 10,
                  opacity: 1,
                  paddingTop: 10,
                  paddingRight: 16,
                  paddingBottom: 10,
                  paddingLeft: 16,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  color: "#0F1011",
                }}
              >
                {trialCopy.ctaLabel}
              </button>
              <p
                className="mt-3 text-center capitalize"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: "10px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#565758",
                }}
              >
                {trialCopy.footerNote}
              </p>
            </div>

            {/* Right: Video container - 60% (wider) */}
            <div className="min-w-0 p-4 sm:p-5 flex flex-col gap-4 bg-white border-l border-gray-100 w-full sm:w-[60%] sm:flex-shrink-0">
              {/* Video container: green gradient background */}
              <div
                className="relative overflow-hidden rounded-lg box-border"
                style={{
                  width: "100%",
                  maxWidth: 366,
                  height: 255,
                  borderRadius: 8,
                  opacity: 1,
                  boxSizing: "border-box",
                  padding: 12,
                  background: videoBgGradient,
                }}
              >
                <div
                  className="absolute overflow-hidden rounded-[6px]"
                  style={{
                    top: 12,
                    left: 12,
                    right: 12,
                    bottom: 12,
                    background: videoBgGradient,
                  }}
                >
                  <img
                    src={homepageCampaignPreviewUrl}
                    alt="Campaign tab preview"
                    className="absolute inset-0 w-full h-full object-cover z-[1]"
                    style={{ backgroundColor: "transparent" }}
                  />
                </div>
              </div>

              {/* Book a call card */}
              <div
                className="flex flex-col bg-white shadow-sm box-border"
                style={{
                  width: 366,
                  maxWidth: "100%",
                  height: 136,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  padding: 12,
                  gap: 10,
                  opacity: 1,
                  boxSizing: "border-box",
                }}
              >
                <h3
                  className="capitalize text-gray-900 flex-shrink-0"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: "18px",
                    letterSpacing: "0%",
                  }}
                >
                  Book A Call With Our Team
                </h3>
                <p
                  className="flex-1 min-h-0"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    color: "#565758",
                  }}
                >
                  Want to learn more? Schedule a call with our founder for a demo.
                </p>
                <button
                  onClick={onScheduleCall}
                  className="hover:bg-gray-50 transition-colors flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: 342,
                    maxWidth: "100%",
                    height: 38,
                    borderRadius: 8,
                    border: "1px solid #000000",
                    gap: 10,
                    opacity: 1,
                    paddingTop: 10,
                    paddingRight: 16,
                    paddingBottom: 10,
                    paddingLeft: 16,
                    boxSizing: "border-box",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#0F1011",
                  }}
                >
                  Schedule a call demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default FreeTrialModal;
